import { apiService, ApiResponse } from './api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants';

// Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  turnstileToken?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  companyName?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  acceptedTerms?: boolean;
  agreeToTerms?: boolean;
  turnstileToken?: string;
  website?: string;
  referralCode?: string | null;
  anonymousId?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  firstTouch?: Record<string, string | null>;
  lastTouch?: Record<string, string | null>;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresIn?: number;
  requiresEmailVerification?: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

// Authentication Service Class
class AuthService {
  // Login
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await apiService.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    
    if (response.success && response.data?.token) {
      // Set the auth token for future requests
      apiService.setAuthToken(response.data.token);
      
      // Store token and user data in localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    
    return response;
  }

  // Register
  async register(userData: RegisterRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await apiService.post<LoginResponse>(API_ENDPOINTS.AUTH.REGISTER, userData);
    
    if (response.success && response.data?.token) {
      // Set the auth token for future requests
      apiService.setAuthToken(response.data.token);
      
      // Store token and user data in localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    
    return response;
  }

  // Get current user profile
  async getProfile(): Promise<ApiResponse<User>> {
    return apiService.get<User>(API_ENDPOINTS.AUTH.PROFILE);
  }

  // Update user profile
  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiService.put<User>(API_ENDPOINTS.AUTH.PROFILE, userData);
  }

  // Forgot password
  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<{ message: string }>> {
    return apiService.post<{ message: string }>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, data);
  }

  // Reset password
  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<{ message: string }>> {
    return apiService.post<{ message: string }>(API_ENDPOINTS.AUTH.RESET_PASSWORD, data);
  }

  // Verify email
  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.post<{ message: string }>(API_ENDPOINTS.AUTH.VERIFY_EMAIL, { token });
  }

  // Logout
  async logout(): Promise<ApiResponse<{ message: string }>> {
    const response = await apiService.post<{ message: string }>(API_ENDPOINTS.AUTH.LOGOUT);
    
    // Clear local storage and auth token regardless of response
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    apiService.clearAuthToken();
    
    return response;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    return !!token;
  }

  // Get stored user data
  getCurrentUser(): User | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  // Initialize auth token from localStorage
  initializeAuth(): void {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      apiService.setAuthToken(token);
    }
  }

  // Validate token (check if still valid)
  async validateToken(): Promise<boolean> {
    try {
      const response = await this.getProfile();
      return response.success;
    } catch (error) {
      this.logout();
      return false;
    }
  }
}

export const authService = new AuthService();
